import React from 'react'

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    Typography,
    Button,
    makeStyles,
    Theme,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import PhoneIcon from '@material-ui/icons/Phone'
import LinkIcon from '@material-ui/icons/Link'

import { useProspectProviderSnapshot } from '../../../../hooks/Outreach/useProspectProviderSnapshot'
import { Prospect } from '../../../../models/Outreach/types'
import { prospectHasRevealedContact } from '../../../Outbound/Prospects/utils/prospectContact'

interface PoolProspectDetailDialogProps {
    open: boolean
    prospect: Prospect | null
    onClose: () => void
}

const useStyles = makeStyles((theme: Theme) => ({
    dialogPaper: {
        maxWidth: 720,
    },
    dialogTitle: {
        paddingBottom: theme.spacing(1),
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontSize: '0.875rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1.5),
    },
    fieldRow: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1),
        alignItems: 'flex-start',
    },
    fieldLabel: {
        minWidth: 140,
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    fieldValue: {
        fontSize: '0.875rem',
        wordBreak: 'break-word',
    },
    educationCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
    },
    chipRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
    },
    statusChip: {
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
    },
    rawJson: {
        margin: 0,
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius,
        fontSize: '0.75rem',
        overflow: 'auto',
        maxHeight: 360,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    emptyState: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        fontStyle: 'italic',
    },
}))

function formatLabel(key: string): string {
    return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function formatScalar(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'number') return String(value)
    if (typeof value === 'string') {
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
            const parsed = new Date(value)
            if (!Number.isNaN(parsed.getTime())) {
                return parsed.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                })
            }
        }
        return value
    }
    return JSON.stringify(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const FieldRow: React.FC<{ label: string; value: React.ReactNode }> = ({
    label,
    value,
}) => {
    const classes = useStyles()
    return (
        <Box className={classes.fieldRow}>
            <Typography className={classes.fieldLabel}>{label}</Typography>
            <Box className={classes.fieldValue}>{value}</Box>
        </Box>
    )
}

const PayloadSection: React.FC<{ payload: Record<string, unknown> }> = ({
    payload,
}) => {
    const classes = useStyles()
    const education = payload.education_background
    const scalarEntries = Object.entries(payload).filter(
        ([key, value]) =>
            key !== 'education_background' &&
            (typeof value === 'string' ||
                typeof value === 'number' ||
                typeof value === 'boolean' ||
                value === null),
    )
    const arrayEntries = Object.entries(payload).filter(
        ([key, value]) =>
            key !== 'education_background' &&
            Array.isArray(value) &&
            value.every(
                (item) =>
                    typeof item === 'string' ||
                    typeof item === 'number' ||
                    item === null,
            ),
    )
    const objectEntries = Object.entries(payload).filter(
        (entry): entry is [string, Record<string, unknown>] =>
            entry[0] !== 'education_background' &&
            isRecord(entry[1]) &&
            !Array.isArray(entry[1]),
    )
    const otherEntries = Object.entries(payload).filter(
        ([key, value]) =>
            key !== 'education_background' &&
            !scalarEntries.some(([k]) => k === key) &&
            !arrayEntries.some(([k]) => k === key) &&
            !objectEntries.some(([k]) => k === key),
    )

    return (
        <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
                Provider data
            </Typography>

            {scalarEntries.map(([key, value]) => (
                <FieldRow
                    key={key}
                    label={formatLabel(key)}
                    value={formatScalar(value)}
                />
            ))}

            {arrayEntries.map(([key, value]) => (
                <FieldRow
                    key={key}
                    label={formatLabel(key)}
                    value={
                        (value as unknown[]).length ? (
                            <Box className={classes.chipRow}>
                                {(value as unknown[]).map((item, index) => (
                                    <Chip
                                        key={`${key}-${index}`}
                                        size="small"
                                        label={formatScalar(item)}
                                    />
                                ))}
                            </Box>
                        ) : (
                            '—'
                        )
                    }
                />
            ))}

            {Array.isArray(education) && education.length > 0 && (
                <Box mt={1} mb={1}>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginBottom: 8 }}
                    >
                        Education
                    </Typography>
                    {education.map((entry, index) =>
                        isRecord(entry) ? (
                            <Box
                                key={`edu-${index}`}
                                className={classes.educationCard}
                            >
                                {Object.entries(entry).map(([key, value]) => (
                                    <FieldRow
                                        key={key}
                                        label={formatLabel(key)}
                                        value={formatScalar(value)}
                                    />
                                ))}
                            </Box>
                        ) : null,
                    )}
                </Box>
            )}

            {objectEntries.map(([key, value]) => (
                <Box key={key} mb={1}>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginBottom: 4 }}
                    >
                        {formatLabel(key)}
                    </Typography>
                    <Box className={classes.educationCard}>
                        {Object.entries(value).map(
                            ([nestedKey, nestedValue]) => (
                                <FieldRow
                                    key={nestedKey}
                                    label={formatLabel(nestedKey)}
                                    value={formatScalar(nestedValue)}
                                />
                            ),
                        )}
                    </Box>
                </Box>
            ))}

            {otherEntries.map(([key, value]) => (
                <FieldRow
                    key={key}
                    label={formatLabel(key)}
                    value={
                        <pre className={classes.rawJson}>
                            {JSON.stringify(value, null, 2)}
                        </pre>
                    }
                />
            ))}
        </Box>
    )
}

export const PoolProspectDetailDialog: React.FC<PoolProspectDetailDialogProps> = ({
    open,
    prospect,
    onClose,
}) => {
    const classes = useStyles()
    const prospectId = open && prospect ? prospect.id : null
    const { data: snapshot, isLoading, isError } = useProspectProviderSnapshot(
        prospectId,
    )

    if (!prospect) return null

    const isProspect = prospectHasRevealedContact(prospect)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            classes={{ paper: classes.dialogPaper }}
        >
            <DialogTitle className={classes.dialogTitle}>
                <Box display="flex" alignItems="center" gridGap={12}>
                    <Typography variant="h6">{prospect.name}</Typography>
                    <Chip
                        size="small"
                        label={isProspect ? 'Prospect' : 'Target'}
                        color={isProspect ? 'primary' : 'default'}
                        className={classes.statusChip}
                    />
                </Box>
                {prospect.school && (
                    <Typography variant="body2" color="textSecondary">
                        {prospect.school.effective_display_name}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent dividers>
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Contact info
                    </Typography>
                    <FieldRow
                        label="Grad year"
                        value={prospect.graduationYear ?? '—'}
                    />
                    <FieldRow
                        label="Email"
                        value={
                            prospect.email?.value ? (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gridGap={6}
                                >
                                    <MailOutlineIcon
                                        fontSize="small"
                                        color="action"
                                    />
                                    {prospect.email.value}
                                </Box>
                            ) : (
                                <Typography className={classes.emptyState}>
                                    {prospect.email?.last_attempted
                                        ? 'Not found'
                                        : 'Needs enrichment'}
                                </Typography>
                            )
                        }
                    />
                    <FieldRow
                        label="Phone"
                        value={
                            prospect.phone?.value ? (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gridGap={6}
                                >
                                    <PhoneIcon
                                        fontSize="small"
                                        color="action"
                                    />
                                    {prospect.phone.value}
                                </Box>
                            ) : (
                                <Typography className={classes.emptyState}>
                                    {prospect.phone?.last_attempted
                                        ? 'Not found'
                                        : 'Needs enrichment'}
                                </Typography>
                            )
                        }
                    />
                    <FieldRow
                        label="LinkedIn"
                        value={
                            prospect.linkedinProfile?.value ? (
                                <Link
                                    href={prospect.linkedinProfile.value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gridGap={6}
                                    >
                                        <LinkIcon fontSize="small" />
                                        View profile
                                    </Box>
                                </Link>
                            ) : (
                                <Typography className={classes.emptyState}>
                                    {prospect.linkedinProfile?.last_attempted
                                        ? 'Not found'
                                        : 'Needs enrichment'}
                                </Typography>
                            )
                        }
                    />
                </Box>

                <Divider />

                {isLoading && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={4}
                    >
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!isLoading && isError && (
                    <Box className={classes.section} pt={2}>
                        <Typography className={classes.emptyState}>
                            No provider snapshot is stored for this person yet.
                        </Typography>
                    </Box>
                )}

                {!isLoading && snapshot && (
                    <>
                        <Box className={classes.section} pt={2}>
                            <Typography className={classes.sectionTitle}>
                                Snapshot metadata
                            </Typography>
                            <FieldRow
                                label="Provider"
                                value={formatLabel(snapshot.provider)}
                            />
                            {snapshot.emails_at_ingest.length > 0 && (
                                <FieldRow
                                    label="Emails at ingest"
                                    value={
                                        <Box className={classes.chipRow}>
                                            {snapshot.emails_at_ingest.map(
                                                (email) => (
                                                    <Chip
                                                        key={email}
                                                        size="small"
                                                        label={email}
                                                    />
                                                ),
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                            <FieldRow
                                label="Last updated"
                                value={formatScalar(snapshot.modified_date)}
                            />
                        </Box>

                        <Divider />

                        <Box pt={2}>
                            <PayloadSection payload={snapshot.payload} />
                        </Box>

                        <Accordion elevation={0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2">
                                    View raw JSON
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <pre className={classes.rawJson}>
                                    {JSON.stringify(snapshot.payload, null, 2)}
                                </pre>
                            </AccordionDetails>
                        </Accordion>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
